import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, XCircle, ArrowRight, MessageCircle } from "lucide-react";

import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { useCart } from "../context/CartContext";
import { getOrder } from "../lib/api";
import { formatPrice } from "../lib/filters";
import { WHATSAPP_URL } from "../lib/site";

const SUCCESS = "SUCCESS";
const FAILED = "FAILED";
const LAST_ORDER_KEY = "bathlane_last_oid";

export default function OrderConfirmation() {
  const [params] = useSearchParams();
  const { clear } = useCart();
  const orderId = params.get("oid") || sessionStorage.getItem(LAST_ORDER_KEY);
  const ost = params.get("ost");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (orderId) sessionStorage.removeItem(LAST_ORDER_KEY);
  }, [orderId]);

  useEffect(() => {
    if (ost === SUCCESS) clear();
  }, [ost, clear]);

  useEffect(() => {
    if (order?.status === SUCCESS) clear();
  }, [order, clear]);

  useEffect(() => {
    if (!orderId) return;
    let tries = 0;
    const fetchOrder = async () => {
      try {
        const o = await getOrder(orderId);
        setOrder(o);
        if (![SUCCESS, FAILED].includes(o.status) && tries < 3) {
          tries += 1;
          setTimeout(fetchOrder, 1500);
        }
      } catch {
        setOrder(null);
      }
    };
    fetchOrder();
  }, [orderId]);

  const serverStatus = order?.status ?? null;
  const status = ost || serverStatus || "INITIATED";

  const headline =
    status === SUCCESS
      ? "Your order is confirmed"
      : status === FAILED
        ? "Your order wasn't completed"
        : "Order pending";

  const body =
    status === SUCCESS
      ? "Thank you for your order. We'll confirm dispatch timelines and Jaipur installation support shortly."
      : status === FAILED
        ? "It looks like the payment wasn't completed. No charges were made — you can try again or reach us on WhatsApp."
        : "We're confirming your order with our team. You'll receive an update on WhatsApp shortly.";

  const Icon =
    status === SUCCESS ? CheckCircle2 : status === FAILED ? XCircle : Clock;

  return (
    <div data-testid="order-confirmation" className="bg-ivory min-h-screen">
      <Header />

      <main className="pt-28 md:pt-32 pb-28 lg:pb-24">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-center"
          >
            <span
              data-testid="order-status-icon"
              className={`grid place-items-center h-20 w-20 rounded-full ${
                status === SUCCESS
                  ? "bg-stone text-charcoal"
                  : status === FAILED
                    ? "bg-stone text-taupe"
                    : "bg-stone text-taupe"
              }`}
            >
              <Icon size={34} strokeWidth={1.3} />
            </span>

            <h1 className="font-serif font-light text-charcoal text-4xl md:text-5xl mt-8 tracking-tight">
              {headline}
            </h1>

            {orderId && (
              <p data-testid="order-id" className="mt-3 eyebrow text-taupe">
                Order ID · {orderId}
              </p>
            )}

            <p className="mt-5 max-w-md text-charcoal/65 leading-relaxed">{body}</p>

            <Link
              to="/orders"
              data-testid="view-all-orders"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-charcoal link-sweep"
            >
              View all your orders <ArrowRight size={14} strokeWidth={1.5} />
            </Link>

            {order?.items?.length > 0 && (
              <div
                data-testid="order-items"
                className="mt-8 w-full max-w-sm border border-beige rounded-sm divide-y divide-beige"
              >
                {order.items.map((i, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-4 px-5 py-4 text-left">
                    <span className="text-sm text-charcoal truncate">{i.name}</span>
                    <span className="text-sm text-charcoal font-medium whitespace-nowrap">
                      {formatPrice(Number(i.price) || 0)}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-4 px-5 py-4 text-left">
                  <span className="text-sm font-medium text-charcoal">Total</span>
                  <span data-testid="order-total" className="text-sm font-serif text-lg text-charcoal">
                    {formatPrice(
                      typeof order.total === "number"
                        ? order.total
                        : order.items.reduce((s, i) => s + (Number(i.price) || 0) * (i.qty || 1), 0)
                    )}
                  </span>
                </div>
              </div>
            )}

            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/vanities"
                data-testid="continue-shopping"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-charcoal text-ivory px-8 py-4 text-sm font-medium btn-fill hover:text-charcoal transition-colors duration-500"
              >
                Continue Shopping <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <a
                href={WHATSAPP_URL(
                  orderId ? `Hi BathLane, I just placed order ${orderId} — can you confirm the dispatch timeline?` : "Hi BathLane, I need help with my recent order."
                )}
                target="_blank"
                rel="noreferrer"
                data-testid="order-whatsapp"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-charcoal px-8 py-4 text-sm font-medium text-charcoal hover:bg-stone/60 transition-colors duration-300"
              >
                <MessageCircle size={16} strokeWidth={1.5} /> Ask on WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
